/**
 * Constants and utility functions for Promotional Codes
 */

// Discount Types
export const DISCOUNT_TYPES = {
  SETUP_FEE_ONLY: "setup_fee_only",
  SETUP_AND_FIRST_MONTH: "setup_fee_and_first_month",
  CUSTOM_SETUP_FEE_DISCOUNT: "custom_discount",
};

export const DISCOUNT_TYPE_LABELS = {
  [DISCOUNT_TYPES.SETUP_FEE_ONLY]: "Waive Setup Fee Only",
  [DISCOUNT_TYPES.SETUP_AND_FIRST_MONTH]: "Waive Setup Fee + First Month",
  [DISCOUNT_TYPES.CUSTOM_SETUP_FEE_DISCOUNT]: "Custom Setup Fee Discount",
};

export const DISCOUNT_TYPE_OPTIONS = [
  { value: DISCOUNT_TYPES.SETUP_FEE_ONLY, label: DISCOUNT_TYPE_LABELS[DISCOUNT_TYPES.SETUP_FEE_ONLY] },
  { value: DISCOUNT_TYPES.SETUP_AND_FIRST_MONTH, label: DISCOUNT_TYPE_LABELS[DISCOUNT_TYPES.SETUP_AND_FIRST_MONTH] },
  { value: DISCOUNT_TYPES.CUSTOM_SETUP_FEE_DISCOUNT, label: DISCOUNT_TYPE_LABELS[DISCOUNT_TYPES.CUSTOM_SETUP_FEE_DISCOUNT] },
];

// Coupon Types
export const COUPON_TYPES = {
  GLOBAL: "global",
  PARTICULAR: "particular",
};

export const COUPON_TYPE_LABELS = {
  [COUPON_TYPES.GLOBAL]: "Global",
  [COUPON_TYPES.PARTICULAR]: "Particular",
};

export const COUPON_TYPE_OPTIONS = [
  { value: COUPON_TYPES.GLOBAL, label: COUPON_TYPE_LABELS[COUPON_TYPES.GLOBAL] },
  { value: COUPON_TYPES.PARTICULAR, label: COUPON_TYPE_LABELS[COUPON_TYPES.PARTICULAR] },
];

// Status Values
export const CODE_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  EXPIRED: "expired",
};

export const STATUS_LABELS = {
  [CODE_STATUS.ACTIVE]: "Active",
  [CODE_STATUS.INACTIVE]: "Inactive",
  [CODE_STATUS.EXPIRED]: "Expired",
};

export const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: CODE_STATUS.ACTIVE, label: STATUS_LABELS[CODE_STATUS.ACTIVE] },
  { value: CODE_STATUS.INACTIVE, label: STATUS_LABELS[CODE_STATUS.INACTIVE] },
  { value: CODE_STATUS.EXPIRED, label: STATUS_LABELS[CODE_STATUS.EXPIRED] },
];

// Badge color mapping for status
export const getStatusBadgeVariant = (status) => {
  switch (status) {
    case CODE_STATUS.ACTIVE:
      return "success";
    case CODE_STATUS.INACTIVE:
      return "secondary";
    case CODE_STATUS.EXPIRED:
      return "danger";
    default:
      return "secondary";
  }
};

// Badge color mapping for discount types
export const getDiscountTypeBadgeVariant = (discountType) => {
  switch (discountType) {
    case DISCOUNT_TYPES.SETUP_FEE_ONLY:
      return "primary";
    case DISCOUNT_TYPES.SETUP_AND_FIRST_MONTH:
      return "info";
    case DISCOUNT_TYPES.CUSTOM_SETUP_FEE_DISCOUNT:
      return "warning";
    default:
      return "secondary";
  }
};

// Badge color mapping for coupon types
export const getCouponTypeBadgeVariant = (couponType) => {
  switch (couponType) {
    case COUPON_TYPES.GLOBAL:
      return "success";
    case COUPON_TYPES.PARTICULAR:
      return "warning";
    default:
      return "secondary";
  }
};

/**
 * Format promotional code (uppercase, trim)
 */
export const formatPromotionalCode = (code) => {
  if (!code) return "";
  return code.toString().trim().toUpperCase();
};

/**
 * Validate code format
 * @param {string} code - The code to validate
 * @returns {object} - { valid: boolean, error: string }
 */
export const validateCodeFormat = (code) => {
  if (!code || code.trim().length === 0) {
    return { valid: false, error: "Code is required" };
  }
  const trimmed = code.trim();
  if (trimmed.length < 3) {
    return { valid: false, error: "Code must be at least 3 characters" };
  }
  if (trimmed.length > 50) {
    return { valid: false, error: "Code must be no more than 50 characters" };
  }
  // Allow alphanumeric, hyphens, and underscores
  if (!/^[A-Z0-9_-]+$/i.test(trimmed)) {
    return { valid: false, error: "Code can only contain letters, numbers, hyphens, and underscores" };
  }
  return { valid: true, error: null };
};

/**
 * Copy code to clipboard
 */
export const copyCodeToClipboard = async (code) => {
  try {
    await navigator.clipboard.writeText(code);
    return true;
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = code;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
      document.body.removeChild(textArea);
      return true;
    } catch (err) {
      document.body.removeChild(textArea);
      return false;
    }
  }
};

/**
 * Calculate status based on expiry date and current status
 * @param {object} code - Promotional code object
 * @returns {string} - Calculated status
 */
export const calculateCodeStatus = (code) => {
  // If already inactive, return inactive
  if (code.status === CODE_STATUS.INACTIVE) {
    return CODE_STATUS.INACTIVE;
  }

  // Check if expired based on expiry date
  if (code.expiresAt) {
    const now = new Date();
    const expiryDate = new Date(code.expiresAt);
    if (expiryDate < now) {
      return CODE_STATUS.EXPIRED;
    }
  }

  // Return the status from the code, defaulting to active
  return code.status || CODE_STATUS.ACTIVE;
};

/**
 * Format usage count display (e.g., "5/10" or "5/∞")
 */
export const formatUsageCount = (usedCount, maxUses) => {
  const used = usedCount || 0;
  if (maxUses === null || maxUses === undefined) {
    return `${used}/∞`;
  }
  return `${used}/${maxUses}`;
};


